import React, { useState, useEffect, useContext } from 'react'
import { LocalizationContext } from '../../../shared/contexts'
import { formatTenantDateTime } from '../../../../utils/formatTenantDateTime'
import axios from 'axios'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Typography
} from '@mui/material'

import InvalidateVoucherModal from '../../../admin/vouchers/InvalidateVoucherModal'
import VoucherTimeline, { VoucherSmsEvent } from '../VoucherTimeline'
import { voucherReasonLabel } from '../../../shared/voucherLabels'

interface VoucherRow {
  id: number
  token: string
  status: string
  reason: string
  initiator: string | null
  issued_by: string | null
  chain_depth: number
  note: string | null
  customer_phone: string | null
  source_order_id: number | null
  source_queued_order_id: number | null
  source_meal_sequence_number: number | null
  source_meal_disposal_reason: string | null
  redeemed_order_id: number | null
  redeemed_order_number: string | null
  expires_at: string | null
  redeemed_at: string | null
  invalidated_at: string | null
  invalidated_reason: string | null
  invalidated_by: string | null
  invalidation_note: string | null
  created_at: string | null
  sms_events: VoucherSmsEvent[]
}

const statusChipColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'active') return 'success'
  if (status === 'redeemed') return 'warning'
  if (['expired', 'invalidated'].includes(status)) return 'error'
  return 'default'
}

interface Props {
  basePath: string
  orderId: number
  canCreate: boolean
  canInvalidate: boolean
}

const VouchersSection = ({ basePath, orderId, canCreate, canInvalidate }: Props) => {
  const [rows, setRows] = useState<VoucherRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [invalidating, setInvalidating] = useState<VoucherRow | null>(null)
  const [expandedIds, setExpandedIds] = useState<number[]>([])
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const localization = useContext(LocalizationContext)
  const formatDateTime = (value: string | null) => formatTenantDateTime(value, localization)

  useEffect(() => {
    axios.get(`${basePath}/vouchers`)
      .then(({ data }) => {
        setRows(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [basePath])

  const toggleExpanded = (id: number, expand: boolean) => {
    setExpandedIds((prev) => (expand ? [...prev, id] : prev.filter((v) => v !== id)))
  }

  const copyToken = (voucher: VoucherRow) => {
    navigator.clipboard.writeText(voucher.token)
    setCopiedId(voucher.id)
    setTimeout(() => setCopiedId((prev) => (prev === voucher.id ? null : prev)), 2000)
  }

  const resendSms = (voucher: VoucherRow) => {
    setError(null)
    axios.post(`/admin/vouchers/${voucher.id}/resend_sms`)
      .then(() => window.alert(`Voucher SMS re-sent to ${voucher.customer_phone || 'the customer'}.`))
      .catch((err) => setError(err.response?.data?.error || 'Could not resend the voucher SMS.'))
  }

  const applyInvalidation = (voucher: VoucherRow, updated: Record<string, unknown>) => {
    setRows((prev) => (prev || []).map((row) => (row.id === voucher.id ? { ...row, ...updated } : row)))
    setInvalidating(null)
  }

  // The same voucher can relate to this order in two ways: it was issued because
  // a meal on this order failed, or the customer spent it to place this order.
  const relation = (row: VoucherRow) => {
    if (row.source_order_id === orderId) {
      return row.source_queued_order_id
        ? `issued for meal #${row.source_queued_order_id}`
        : 'issued for this order'
    }
    if (row.redeemed_order_id === orderId) return 'spent on this order'

    return null
  }

  const usageHint = (row: VoucherRow) => {
    if (row.redeemed_at) return `used ${formatDateTime(row.redeemed_at)}`
    if (row.invalidated_at) return `invalidated ${formatDateTime(row.invalidated_at)}`
    if (row.status === 'expired') return row.expires_at ? `expired ${formatDateTime(row.expires_at)}` : 'expired'
    return row.expires_at ? `not used yet · expires ${formatDateTime(row.expires_at)}` : 'not used yet'
  }

  if (loading) {
    return <Box className='text-center py-3'><CircularProgress size={24} /></Box>
  }

  if (!rows || rows.length === 0) {
    return <Typography variant='body2' color='text.secondary'>No vouchers related to this order.</Typography>
  }

  return (
    <>
      {invalidating && (
        <InvalidateVoucherModal
          voucher={invalidating}
          onClose={() => setInvalidating(null)}
          onInvalidated={(updated) => applyInvalidation(invalidating, updated)}
        />
      )}
      {error && <div className='alert alert-danger'>{error}</div>}
      {rows.map((row, index) => {
        const expanded = expandedIds.includes(row.id)

        return (
          <Accordion
            key={row.id}
            expanded={expanded}
            onChange={(_, isExpanded) => toggleExpanded(row.id, isExpanded)}
            disableGutters
            className='voucher-accordion'
          >
            <AccordionSummary>
              <div className='voucher-accordion-summary'>
                <span className='voucher-name'>Voucher #{index + 1}</span>
                {row.chain_depth > 0 && <span className='text-muted small'>re-issue</span>}
                <Chip label={row.status} color={statusChipColor(row.status)} size='small' />
                <span className='text-muted small voucher-summary-reason'>
                  {voucherReasonLabel(row)}
                  {relation(row) ? ` · ${relation(row)}` : ''}
                </span>
                <span className='text-muted small voucher-summary-usage'>{usageHint(row)}</span>
                <span className='btn btn-sm bw-button voucher-summary-toggle'>
                  {expanded ? 'Hide details' : 'Show details'}
                </span>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <VoucherTimeline
                voucher={row}
                header={(
                  <div className='voucher-token-line'>
                    <span className='text-muted small'>Voucher token:</span>
                    <span className='voucher-token'>{row.token}
                      <Tooltip title={copiedId === row.id ? 'Copied!' : 'Copy voucher token'} placement='top' arrow>
                        <button type='button' className='voucher-copy-button' onClick={() => copyToken(row)}>
                          <i className={copiedId === row.id ? 'fa fa-check' : 'fa fa-copy'} />
                        </button>
                      </Tooltip>
                    </span>
                  </div>
                )}
              />
              {row.status === 'active' && (canCreate || canInvalidate) && (
                <div className='voucher-actions'>
                  {row.customer_phone && canCreate && (
                    <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => resendSms(row)}>
                      Resend SMS
                    </button>
                  )}
                  {canInvalidate && (
                    <button type='button' className='btn btn-sm btn-outline-danger ms-auto' onClick={() => setInvalidating(row)}>
                      Invalidate voucher
                    </button>
                  )}
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        )
      })}
    </>
  )
}

export default VouchersSection
